import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Minus,
  Plus,
  ShoppingCart,
  ShieldCheck,
  Truck,
  Phone,
} from "lucide-react";
import { getProduct as staticGetProduct, PHONE_DISPLAY, products as staticProducts } from "../data/products";
import { useProducts } from "../context/ProductsContext";
import { useCart } from "../context/CartContext";
import { openWhatsApp } from "../lib/whatsapp";
import { ProductCard } from "../components/ProductCard";
import { useSeo } from "../lib/seo";

const SITE_URL = "https://detereco-green.ro";

export default function ProductPage() {
  const { slug } = useParams();
  const { getProduct } = useProducts();
  const product = getProduct(slug) || staticGetProduct(slug);
  const { addItem, openCheckout } = useCart();
  const [qty, setQty] = useState(1);
  const [variantId, setVariantId] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    setQty(1);
    setVariantId(null);
  }, [slug]);

  useSeo({
    title: product
      ? `${product.title} – ${product.price} lei | Detecogreen`
      : "Detecogreen",
    description: product
      ? `${product.short} ${product.description.slice(0, 130)}... Comandă acum pe WhatsApp la +40 (732) 873 176. Transport gratuit, plată cash la curier.`
      : undefined,
    canonical: product ? `${SITE_URL}/produs/${product.slug}` : SITE_URL,
    ogImage: product?.image,
    ogType: "product",
    jsonLd: product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.title,
          image: [product.image],
          description: product.description,
          sku: product.id,
          brand: { "@type": "Brand", name: "Detecogreen" },
          offers: {
            "@type": "Offer",
            url: `${SITE_URL}/produs/${product.slug}`,
            priceCurrency: "RON",
            price: product.price,
            priceValidUntil: "2027-12-31",
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
            seller: { "@type": "Organization", name: "Detecogreen" },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "127",
          },
        }
      : undefined,
  });

  const { products: liveProducts } = useProducts();
  const productList = liveProducts.length > 0 ? liveProducts : staticProducts;
  const related = productList.filter((p) => p.id !== product?.id).slice(0, 3);

  if (!product) return <Navigate to="/" replace />;

  const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
  const selectedVariant = hasVariants
    ? product.variants.find((v) => v.id === variantId)
    : null;

  const buildCartProduct = () => {
    if (!hasVariants) return product;
    return {
      ...product,
      id: `${product.id}-${selectedVariant.id}`,
      title: `${product.title} · ${selectedVariant.label}`,
      variantLabel: selectedVariant.label,
    };
  };

  const handleAddToCart = () => {
    if (hasVariants && !selectedVariant) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      // eslint-disable-next-line no-alert
      import("sonner").then(({ toast }) =>
        toast.error("Alege o aromă înainte de a adăuga în coș"),
      );
      return;
    }
    addItem(buildCartProduct(), qty);
    openCheckout();
  };

  const handleWhatsApp = () => {
    openWhatsApp();
  };

  return (
    <main data-testid="product-page" className="bg-white">
      <div className="bg-sky-50/60 border-b border-sky-100">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center gap-2 text-sm">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-1.5 text-slate-600 hover:text-cyan-600 font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Înapoi la magazin
          </Link>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div className="relative">
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-white border border-sky-100">
              {product.discount && (
                <div className="absolute top-5 left-5 z-10 bg-rose-500 text-white text-sm font-black px-3 py-1.5 rounded-full">
                  -{product.discount}%
                </div>
              )}
              {product.badge && (
                <div className="absolute top-5 right-5 z-10 bg-white text-cyan-700 text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-full border border-cyan-100">
                  {product.badge}
                </div>
              )}
              <img
                src={product.image}
                alt={product.title}
                referrerPolicy="no-referrer"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <div className="flex flex-col">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-600 mb-3">
              Produs premium · Fabricat în România
            </div>
            <h1
              data-testid="product-title"
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-[1.1]"
            >
              {product.title}
            </h1>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              {product.short}
            </p>

            <div className="mt-7 flex items-baseline gap-3">
              <span className="font-heading text-4xl md:text-5xl font-black text-slate-900">
                {product.price} lei
              </span>
              {product.oldPrice && (
                <>
                  <span className="text-lg line-through text-slate-400">
                    {product.oldPrice} lei
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 text-xs font-bold">
                    Economisești {(product.oldPrice - product.price).toFixed(2)} lei
                  </span>
                </>
              )}
            </div>

            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {product.highlights.map((h, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm text-slate-700"
                >
                  <span className="w-5 h-5 rounded-full bg-cyan-50 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-cyan-600" strokeWidth={3} />
                  </span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>

            {hasVariants && (
              <div
                data-testid="variant-selector"
                className="mt-7 p-5 rounded-2xl bg-white border-2 border-cyan-100"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-600 text-white text-xs font-black flex items-center justify-center">
                    1
                  </span>
                  <span className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                    Alege aroma
                  </span>
                  {!selectedVariant && (
                    <span className="ml-auto text-xs text-rose-600 font-semibold">
                      Selectează o aromă *
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {product.variants.map((v) => {
                    const active = variantId === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => setVariantId(v.id)}
                        data-testid={`variant-${v.id}`}
                        className={
                          "h-12 rounded-xl border-2 font-bold text-sm transition-all " +
                          (active
                            ? "bg-cyan-600 border-cyan-600 text-white shadow-lg shadow-cyan-200"
                            : "bg-white border-slate-200 text-slate-700 hover:border-cyan-400")
                        }
                      >
                        {v.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-8 p-5 rounded-2xl bg-sky-50 border border-sky-100">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-semibold text-slate-700">
                  Cantitate:
                </span>
                <div className="inline-flex items-center rounded-full bg-white border border-slate-200">
                  <button
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    data-testid="qty-decrease"
                    className="w-10 h-10 flex items-center justify-center text-slate-700 hover:text-cyan-600"
                    aria-label="Scade"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span
                    data-testid="qty-value"
                    className="w-10 text-center font-bold text-slate-900"
                  >
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty((q) => q + 1)}
                    data-testid="qty-increase"
                    className="w-10 h-10 flex items-center justify-center text-slate-700 hover:text-cyan-600"
                    aria-label="Crește"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-sm text-slate-500 ml-auto">
                  Subtotal:{" "}
                  <span className="font-black text-slate-900">
                    {(product.price * qty).toFixed(2)} lei
                  </span>
                </span>
              </div>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <button
                  onClick={handleWhatsApp}
                  data-testid="product-whatsapp-order"
                  className="h-14 rounded-xl bg-[#25d366] hover:bg-[#1da851] text-white font-bold flex items-center justify-center gap-2.5 transition-colors shadow-lg shadow-emerald-200"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                  </svg>
                  Comandă acum pe WhatsApp
                </button>
                <button
                  onClick={handleAddToCart}
                  disabled={hasVariants && !selectedVariant}
                  data-testid="product-add-to-cart"
                  className="h-14 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {hasVariants && !selectedVariant ? "Alege aroma" : "Adaugă în coș"}
                </button>
              </div>

              <a
                href={`tel:${PHONE_DISPLAY.replace(/[\s()]/g, "")}`}
                className="mt-3 w-full h-11 rounded-xl bg-white border-2 border-slate-200 hover:border-cyan-500 text-slate-800 font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="product-call"
              >
                <Phone className="w-4 h-4" />
                Sună acum: {PHONE_DISPLAY}
              </a>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2.5 text-slate-600">
                <Truck className="w-4 h-4 text-cyan-600" />
                Livrare în 24-48h oriunde în România
              </div>
              <div className="flex items-center gap-2.5 text-slate-600">
                <ShieldCheck className="w-4 h-4 text-cyan-600" />
                Plătești cash la curier
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 md:mt-24 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl md:text-3xl font-black text-slate-900 mb-4">
              Descriere produs
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              {product.description}
            </p>
          </div>
          <div className="bg-sky-50 rounded-2xl p-6 border border-sky-100">
            <h3 className="font-heading font-bold text-slate-900 mb-4">
              Cum comand?
            </h3>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-2.5">
                <span className="w-6 h-6 shrink-0 rounded-full bg-cyan-600 text-white font-bold text-xs flex items-center justify-center">
                  1
                </span>
                Apasă „Comandă acum"
              </li>
              <li className="flex gap-2.5">
                <span className="w-6 h-6 shrink-0 rounded-full bg-cyan-600 text-white font-bold text-xs flex items-center justify-center">
                  2
                </span>
                Trimite mesajul pre-completat pe WhatsApp
              </li>
              <li className="flex gap-2.5">
                <span className="w-6 h-6 shrink-0 rounded-full bg-cyan-600 text-white font-bold text-xs flex items-center justify-center">
                  3
                </span>
                Primești coletul acasă
              </li>
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-sky-50/40 py-16 md:py-20 border-t border-sky-100">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <h2 className="font-heading text-2xl md:text-3xl font-black text-slate-900 mb-8">
            S-ar putea să-ți placă și
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
